/**
 * @author        calvin@zumper.com (Calvin Yau)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   App Container
 */

import {
  appMounted,
  initialLocationChanged,
  selectIsMobile,
  selectIsMounted,
  selectWarnOffline,
  setMobile,
  setOnline,
  setWarnOffline,
  updateNow,
} from '@zcode-ui/web-zumper-global-state'
import { PERFORMANCE_METRICS_PAINT } from '@zcode-ui/web-zumper-integrations'
import { APP_HOSTNAME, AnalyticsC, ON_BROWSER, localStorageSingleton } from '@zcode/nr-utils'
import { selectShowMobileAutocomplete } from '@zcode/nr/es/shared/modules/autocomplete/selectors'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware'
import { isMobile } from '@zcode/nr/es/shared/utils/utils'
import { hocCompose } from '@zcode/react-utils'
import type { State } from '@zcode/state-type'
import { debounce } from '@zcode/utils'
import type { Location } from 'history'
import { connect } from 'react-redux'
import { addSuccessButterBar } from '../../actions/butter.action'
import { initLocalizer, isLocalizerKey } from '../../actions/localStorage.action'
import { clearPreviousFiltersCount } from '../../modules/mixpanel/helpers/previousFiltersCountLocalStorage'
import { clearPreviousPage } from '../../modules/mixpanel/helpers/previousPageLocalStorage'
import { selectOpenModal } from '../../modules/modals/selectors'
import { closeModal } from '../../modules/modals/thunks/index'
import { syncTraktorIdFromBlueshift } from '../../modules/traktor/thunks/syncTraktorIdFromBlueshift'
import { trackInitialPageLoad } from '../../modules/traktor/thunks/trackInitialPageLoad'
import { trackPerformanceMetricsEvent } from '../../modules/traktor/thunks/trackPerformanceMetricsEvent'
import type { Dispatch } from '../../modules/types'
import { withHistory } from '../../utils/withHistory'
import { getModalName } from '../ModalWrapper/ModalWrapperContainer'
import { App, type AppProps } from './App'

let isInitialLocation = true

const mapStateToProps = (state: State) => {
  return {
    isMobileAutocompleteOpen: selectShowMobileAutocomplete(state),
  } satisfies Partial<AppProps>
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    onBeforeunload: () => {
      clearPreviousFiltersCount()
      clearPreviousPage()
      localStorageSingleton.del('mapListFilters')
    },
    onLocationChanged: (previousLocation?: Location<unknown>) => {
      if (ON_BROWSER) {
        // update the head's <base> url to current url:
        const base = document.querySelector('base')
        if (base) {
          base.href = `https://${APP_HOSTNAME}${window.location.pathname}`
        }
      }
      if (isInitialLocation) {
        dispatch((_, getState) => {
          const state = getState()
          // Track if we're still on the initial location.
          // Useful for components that don't need to update after hydration until we change routes.
          const mounted = selectIsMounted(state)

          if (mounted) {
            isInitialLocation = false
            dispatch(initialLocationChanged())
            // wait until we change location to use what the client thinks isMobile is instead of in onMount
            // so that we don't risk transitioning from true/false to the opposite while hydrating from SSR to client-side
            const mobile = selectIsMobile(state)
            const realMobile = isMobile()

            if (mobile !== realMobile) {
              dispatch(setMobile(realMobile))
            }
          }
        })
      }
      // Close any open modals
      if (
        previousLocation?.hash.includes('#modal=') &&
        !dispatch(getRouteProps()).location.hash.includes('#modal=')
      ) {
        const previouslyOpenModal = new URLSearchParams(previousLocation.hash.substring(1)).get(
          'modal'
        )
        const currentlyOpenModal = dispatch((_, getState) => selectOpenModal(getState()))
        if (
          previouslyOpenModal &&
          currentlyOpenModal &&
          previouslyOpenModal === getModalName(currentlyOpenModal)
        ) {
          dispatch(closeModal())
        }
      }
      dispatch(updateNow())
    },
    onMount: () => {
      dispatch(initLocalizer())
      dispatch(appMounted())
      dispatch(updateNow())

      const firstSessionTimestamp = localStorageSingleton.getx('firstSessionTimestamp')

      if (!firstSessionTimestamp) {
        const now = Date.now()
        localStorageSingleton.setx('firstSessionTimestamp', now, 30)
      }
      // @ts-expect-error
      if (ON_BROWSER && window.performanceTraktorEvents) {
        // @ts-expect-error
        window.performanceTraktorEvents
          .filter((el: { eventCategory?: unknown }) => {
            return el.eventCategory === AnalyticsC.PERFORMANCE_METRICS
          })
          .forEach((performanceMetric: unknown) => {
            dispatch(
              trackPerformanceMetricsEvent({
                additionalProps: performanceMetric,
                name: PERFORMANCE_METRICS_PAINT,
              })
            )
          })
        // Clear events
        // @ts-expect-error
        window.performanceTraktorEvents = []

        // from now on, dispatch events that are pushed to that array
        // @ts-expect-error
        window.performanceTraktorEvents.push = (performanceMetric: unknown) => {
          dispatch(
            trackPerformanceMetricsEvent({
              additionalProps: performanceMetric,
              name: PERFORMANCE_METRICS_PAINT,
            })
          )
        }
        dispatch(trackInitialPageLoad({}))
      }
      dispatch(syncTraktorIdFromBlueshift())
    },
    onResize: debounce(() => {
      dispatch((_, getState) => {
        const state = getState()
        const mobile = selectIsMobile(state)
        const realMobile = isMobile()
        if (mobile !== realMobile) {
          dispatch(setMobile(realMobile))
        }
      })
    }, 50),
    onSetOffline: () => dispatch(setOnline(false)),
    onSetOnline: () => {
      dispatch((_, getState) => {
        const state = getState()
        const warnedOffline = selectWarnOffline(state)
        warnedOffline &&
          dispatch(addSuccessButterBar({ message: 'Your internet connection has returned!' }))
        dispatch(setOnline(true))
        dispatch(setWarnOffline(false))
      })
    },
    onStorage: (event: StorageEvent) => {
      if (isLocalizerKey(event.key)) {
        dispatch(initLocalizer())
      }
    },
    updateNow: () => {
      dispatch(updateNow())
    },
  } satisfies Partial<AppProps>
}

export const AppContainer = hocCompose(
  withHistory,
  connect(mapStateToProps, mapDispatchToProps),
  App
)
